import { TabidooTablePage } from '@/components/vyroba/TabidooTablePage';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { type NitKontrola, odeslatNaXerox, pdfKontrolaNiti } from '@/lib/tiskNiti';
import { useEffect, useState } from 'react';

// 0 = nit nepotřebujeme — nepočítá se do kalkulace ani do PDF kontroly.
const MIN_MNOZSTVI = ['0', '10', '20', '30', '40'];
const CENA_KEY = 'niti-cena-civky';

/** Hotové PDF kontroly — náhled v nové kartě a odeslání na Xerox v dílně. */
function PdfKontrolaModal({
  pdf,
  pocet,
  onClose,
}: { pdf: Blob; pocet: number; onClose: () => void }) {
  useEscapeKey(onClose);
  const [url] = useState(() => URL.createObjectURL(pdf));
  const [stav, setStav] = useState<'' | 'odesilam' | 'odeslano'>('');
  const [chyba, setChyba] = useState('');
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const odeslat = async () => {
    setStav('odesilam');
    setChyba('');
    try {
      await odeslatNaXerox(pdf);
      setStav('odeslano');
    } catch (e: unknown) {
      setStav('');
      setChyba(e instanceof ApiError ? e.message : 'Odeslání na Xerox selhalo.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-6 reveal text-left">
        <h2 className="text-lg font-bold text-text">PDF kontrola je připravená</h2>
        <p className="text-sm text-text-secondary mt-1">
          {pocet} nití s nastaveným minimálním množstvím, seřazené podle čísla.
        </p>
        <div className="mt-5 flex flex-col gap-3">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 border border-border rounded-xl px-5 py-4 hover:bg-surface-hover transition-colors"
          >
            <span className="text-2xl">🔍</span>
            <span>
              <span className="block text-base font-bold text-text">Otevřít PDF</span>
              <span className="block text-sm text-text-secondary">
                Náhled v nové kartě — odtud jde i stáhnout nebo vytisknout.
              </span>
            </span>
          </a>
          <button
            type="button"
            onClick={() => void odeslat()}
            disabled={stav !== ''}
            className="flex items-center gap-3 w-full text-left border border-border rounded-xl px-5 py-4 hover:bg-surface-hover transition-colors cursor-pointer disabled:cursor-default disabled:opacity-70"
          >
            <span className="text-2xl">🖨️</span>
            <span>
              <span className="block text-base font-bold text-text">
                {stav === 'odeslano'
                  ? 'Odesláno na Xerox ✓'
                  : stav === 'odesilam'
                    ? 'Odesílám…'
                    : 'Odeslat na Xerox v dílně'}
              </span>
              <span className="block text-sm text-text-secondary">
                Výkaz se zařadí do fronty a vytiskne se na dílenské tiskárně.
              </span>
            </span>
          </button>
          {chyba && <p className="text-sm text-danger">{chyba}</p>}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}

/** Kalkulace minimálních zásob — kolik cívek je zaznačeno a co to stojí. */
function KalkulaceMinima({
  rows,
  reload,
}: {
  rows: { values: Record<string, string> }[];
  reload: () => Promise<void>;
}) {
  const [cena, setCena] = useState(() => localStorage.getItem(CENA_KEY) ?? '');
  const vybrane = rows.filter((r) => Number(r.values.minMnozstvi) > 0);
  const nepotrebne = rows.filter((r) => r.values.minMnozstvi === '0').length;
  const kusu = vybrane.reduce((s, r) => s + Number(r.values.minMnozstvi), 0);
  const cenaKs = Number(cena.replace(',', '.'));
  const investice = cenaKs > 0 ? kusu * cenaKs : null;
  const [pdf, setPdf] = useState<{ blob: Blob; pocet: number } | null>(null);
  const [generuji, setGeneruji] = useState(false);
  const [chyba, setChyba] = useState('');
  // Reset jde na dvě kliknutí (druhé potvrzuje) — vymaže minima u všech nití.
  const [reset, setReset] = useState<'' | 'potvrdit' | 'mazu'>('');
  const nastavenych = rows.filter((r) => r.values.minMnozstvi).length;

  const resetovat = async () => {
    if (reset === '') {
      setReset('potvrdit');
      return;
    }
    setReset('mazu');
    setChyba('');
    try {
      await apiFetch('/api/vyroba/niti/reset-minimum', { method: 'POST' });
      await reload();
    } catch (e: unknown) {
      setChyba(e instanceof ApiError ? e.message : 'Reset se nepovedl.');
    } finally {
      setReset('');
    }
  };

  const vytvoritPdf = async () => {
    const nite: NitKontrola[] = vybrane
      .map((r) => ({
        kod: r.values.kod ?? '',
        barva: r.values.barva ?? '',
        ksProduktu: r.values.__potreba ?? '',
        minimum: r.values.minMnozstvi ?? '',
      }))
      .sort((a, b) => Number(a.kod) - Number(b.kod) || a.kod.localeCompare(b.kod, 'cs'));
    setGeneruji(true);
    setChyba('');
    try {
      setPdf({ blob: await pdfKontrolaNiti(nite), pocet: nite.length });
    } catch {
      setChyba('PDF se nepodařilo vytvořit.');
    } finally {
      setGeneruji(false);
    }
  };
  return (
    <div className="mb-5 bg-surface rounded-xl shadow-card px-5 py-4 flex flex-wrap items-end gap-x-8 gap-y-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-text-muted">Zaznačeno nití</div>
        <div className="text-xl font-bold text-text tabular-nums">
          {vybrane.length}{' '}
          <span className="text-sm font-normal text-text-muted">z {rows.length}</span>
          {nepotrebne > 0 && (
            <span className="block text-xs font-normal text-text-muted">
              {nepotrebne} nepotřebujeme (0)
            </span>
          )}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-text-muted">Minimum celkem</div>
        <div className="text-xl font-bold text-text tabular-nums">
          {kusu.toLocaleString('cs-CZ')} ks
        </div>
      </div>
      <label className="text-sm text-text-secondary">
        <span className="block text-xs uppercase tracking-wider text-text-muted mb-1">
          Cena za cívku (Kč)
        </span>
        <input
          value={cena}
          onChange={(e) => {
            setCena(e.target.value);
            localStorage.setItem(CENA_KEY, e.target.value);
          }}
          inputMode="decimal"
          placeholder="např. 45"
          className="w-28 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text focus:outline-none focus:border-primary"
        />
      </label>
      <div>
        <div className="text-xs uppercase tracking-wider text-text-muted">Investice</div>
        <div className="text-xl font-bold text-primary tabular-nums">
          {investice == null ? '—' : `${Math.round(investice).toLocaleString('cs-CZ')} Kč`}
        </div>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          onClick={() => void resetovat()}
          onBlur={() => reset === 'potvrdit' && setReset('')}
          disabled={reset === 'mazu' || nastavenych === 0}
          className={`font-semibold text-base rounded-xl px-5 py-3 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default ${
            reset === 'potvrdit'
              ? 'bg-danger text-white'
              : 'bg-surface-hover text-text-secondary border border-border hover:bg-border-light'
          }`}
        >
          {reset === 'potvrdit'
            ? `Opravdu smazat ${nastavenych}?`
            : reset === 'mazu'
              ? 'Mažu…'
              : 'Resetovat množství'}
        </button>
        <button
          type="button"
          onClick={() => void vytvoritPdf()}
          disabled={generuji || vybrane.length === 0}
          title={vybrane.length === 0 ? 'Nejdřív u nití vyber minimální množství' : undefined}
          className="bg-success text-white font-bold text-base rounded-xl px-6 py-3 hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-default"
        >
          {generuji ? 'Generuji…' : '📄 PDF kontrola'}
        </button>
      </div>
      {chyba && <p className="w-full text-right text-sm text-danger">{chyba}</p>}
      {pdf && <PdfKontrolaModal pdf={pdf.blob} pocet={pdf.pocet} onClose={() => setPdf(null)} />}
    </div>
  );
}

/**
 * Zelená = minimum vybrané (i 0), oranžová = nit je potřeba na předskladněné
 * kusy, ale minimum ještě nikdo nevybral.
 */
function barvaRadku(v: Record<string, string>): string | undefined {
  if (v.minMnozstvi) return 'bg-[#d1fae5] hover:bg-[#a7f3d0]';
  if (Number(v.__potreba) > 0) return 'bg-[#ffedd5] hover:bg-[#fed7aa]';
  return undefined;
}

/** Databáze nití — zrcadlo Tabidoo tabulky „databazeniti". */
export function DatabazeNitiPage() {
  return (
    <TabidooTablePage
      table="niti"
      storagePrefix="niti"
      title="Databáze nití"
      subtitle="Zrcadlo z Tabidoo. Hledej globálně, filtruj po sloupcích, přehazuj sloupce tažením. Klikni na řádek pro detail a editaci."
      createLabel="Nová nit"
      defaultSort={{ key: 'kod', dir: 'asc' }}
      inlineChoice={{ key: 'minMnozstvi', options: MIN_MNOZSTVI }}
      renderTop={({ rows, reload }) => <KalkulaceMinima rows={rows} reload={reload} />}
      rowColor={barvaRadku}
    />
  );
}
